/**
 * Which dashboard a device gets. Touch decides, not width alone: a narrow desktop window stays a
 * desktop, a touch laptop with a trackpad stays a desktop, and an iPad (whose Safari reports itself
 * as a Mac) is recognised by its touch screen, never by the browser name.
 */
export type FormFactor = "phone" | "tablet" | "desktop";

/** Below this shorter screen side a touch device is a phone. */
export const PHONE_MAX_SHORT_SIDE = 600;

export type FormFactorInput = {
    /** the primary pointer is a finger (`pointer: coarse`) */
    coarsePointer: boolean;
    /** the device reports touch points; iPads in desktop mode only show up here */
    maxTouchPoints: number;
    screenWidth: number;
    screenHeight: number;
};

export type FormFactorOverride = "" | "phone" | "desktop";

export function detectFormFactor(input: FormFactorInput, override: FormFactorOverride = ""): FormFactor {
    const touch = input.coarsePointer || input.maxTouchPoints > 1;
    const shortSide = Math.min(input.screenWidth, input.screenHeight);
    const detected: FormFactor = !touch || !input.coarsePointer ? "desktop" : shortSide < PHONE_MAX_SHORT_SIDE ? "phone" : "tablet";
    return override || detected;
}

export function readFormFactorInput(): FormFactorInput {
    if (typeof window === "undefined") {
        return { coarsePointer: false, maxTouchPoints: 0, screenWidth: 1440, screenHeight: 900 };
    }
    const coarsePointer = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    return {
        coarsePointer,
        maxTouchPoints: navigator.maxTouchPoints || 0,
        screenWidth: window.screen?.width || window.innerWidth,
        screenHeight: window.screen?.height || window.innerHeight,
    };
}

/** `?view=phone`, `?view=desktop` or `?view=auto` in the address sets or clears the override. */
export function overrideFromSearch(search: string): FormFactorOverride | undefined {
    const view = new URLSearchParams(search).get("view");
    if (view === "phone" || view === "desktop") {
        return view;
    }
    if (view === "auto") {
        return "";
    }
    return undefined;
}

/**
 * Reloads the dashboard as the desktop version, or back to the detected one. The address sets the
 * stored override before the first render, so the other shell never mounts in between.
 */
export function openView(view: "desktop" | "auto") {
    window.location.assign(`/?view=${view}`);
}
